import React from 'react';
import {
  ADMIN_DETAILS_FIELDS_UPDATED,
  ProjectDetailsField,
  ProjectDetailsFieldType,
  useProjectDetailsFieldsAdminService
} from 'nekst-api';
import {
  Cell, FilterContext,
  List,
  MassUpdate,
  NewLine, Right,
  Text,
  useShowContextRequired
} from 'ui-builder';
import { useSetupAreaVersionContextRequired } from '../SetupAreaVersionContext';
import {
  GridLayoutWithSort,
  IconDeleteButton,
  SelectForMassUpdateCheckbox
} from 'features/nekst-widgets';
import TextField from '../../../../shared/uibuilder/show/field/TextField';
import ProjectDetailsFieldTypeField from '../../../projectdetails/List/ProjectDetailsFieldTypeField';
import { EditDetailBoxFormButton } from './EditDetailBoxForm';
import { useProjectTypeHelper } from 'features/tasks-forms';

import styles from './DetailsFieldsList.module.scss';
import { DetailFieldsOptionsButton } from './CreateDetailFieldForm';
import { MassUpdateCheckbox } from 'features/tasks-list-feature';
import { DetailFieldsMassUpdate } from './massupdate';
import ProjectTypeFilter from './ProjectTypeFilter';
import SetStartAndEndDateForMarketForm from './SetStartAndEndDateForMarketForm';
import SetCommissionFieldsForMarketForm from './SetCommissionFieldsForMarketForm';

function DeleteFieldButton(props: {
  setupAreaId: number,
  setupAreaVersion: string,
}) {
  const service = useProjectDetailsFieldsAdminService();

  const showContext = useShowContextRequired<ProjectDetailsField>();

  return (
    <IconDeleteButton
      deleteFunc={async () => {
        await service.removeFieldFromSetupAreaVersion(props.setupAreaId, props.setupAreaVersion, showContext.data.id);
      }}
      message="Are you sure you want to delete this detail box?"
    />
  );
}

function ProjectTypesField() {
  const showContext = useShowContextRequired<ProjectDetailsField>();

  const field = showContext.data;

  const projectTypesHelper = useProjectTypeHelper();

  return (
    <Text>{field.projectTypes.map((item) => projectTypesHelper.getLabel(item))
      .join(', ')}</Text>
  );
}


export default function DetailFieldsList() {
  const detailsFieldsService = useProjectDetailsFieldsAdminService();

  const { setupArea, setupAreaVersion } = useSetupAreaVersionContextRequired();


  return (
    <MassUpdate>
      <FilterContext>
        <ProjectTypeFilter />
        <List
          getDataFunc={async (filter) => {
            return detailsFieldsService.getAreaFields(setupArea.id, setupAreaVersion.version, filter)
          }}
          updateOnEvents={[ADMIN_DETAILS_FIELDS_UPDATED]}
          loadDataOnFilterUpdate
        >
          <div>
            <div style={{ display: 'flex', flex: 1, gap: 20 }}>
              <div style={{ flex: 1 }}>
                <SetStartAndEndDateForMarketForm />
              </div>
              <div style={{ flex: 1 }}>
                <SetCommissionFieldsForMarketForm />
              </div>
            </div>
          </div>

          <Right>
            <MassUpdateCheckbox />
          </Right>
          <DetailFieldsMassUpdate />
          <DetailFieldsOptionsButton
            areaId={setupArea.id}
            version={setupAreaVersion.version}
          />
          <GridLayoutWithSort<ProjectDetailsField>
            orderingConfiguration={{
              isEnabled: true,
              updateOrderFunc: async (ids: number[]) => {
                await detailsFieldsService.setOrder(setupArea.id, setupAreaVersion.version, ids);
              },
              isItemDraggable: (item: ProjectDetailsField) => {
                return item.fieldType !== ProjectDetailsFieldType.LONG_TEXT;
              },
              getOrderGroupFunc: (item) => {
                return item.fieldType !== ProjectDetailsFieldType.LONG_TEXT ? 'standard' : 'long_text';
              }
            }}
            noDataMessage="No detail boxes have been created yet."
          >
            <SelectForMassUpdateCheckbox />
            <Cell label="Detail Boxes">
              <TextField source="name" label="Detail Boxes" />
              <NewLine />
              <TextField source="_group.name" noDataText="" className={styles.groupField} />
            </Cell>

            <ProjectDetailsFieldTypeField source="fieldType" label="Type" />
            <Cell label="Transaction Types">
              <ProjectTypesField />
            </Cell>
            <Cell label="Parent Box">
              <TextField source="_parentField.name" />
            </Cell>
            <Cell label="Actions">
              <EditDetailBoxFormButton />
              <DeleteFieldButton
                setupAreaId={setupArea.id}
                setupAreaVersion={setupAreaVersion.version}
              />
            </Cell>
          </GridLayoutWithSort>

        </List>
      </FilterContext>
    </MassUpdate>
  );
}
